import { Modal, TextInput, NumberInput, Group, Button, Stack, Switch, Select, Textarea, CopyButton, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createManualBooking, deleteBookingPayment, updateBooking } from '../../../../redux/reducers/bookingSlice';
import { Booking, BookingPaymentStatus, BookingSource, BookingType } from '../../../../models/booking.model';
import { User } from '../../../../models/user.model';
import { selectUserInfo } from '../../../../redux/selectors/authSelector';
import { selectUsersSelector } from '../../../../redux/selectors/userSelector';
import { selectSettingsSelector } from '../../../../redux/selectors/settingSelector';
import { getAllUsers } from '../../../../redux/reducers/userSlice';
import { Setting, SettingKey } from '../../../../models/setting.model';
import { getShowTimeForDate, getAthensDate, areAthensDatesEqual } from 'utils/helper.utils';
import { DateTime } from 'luxon';
import { selectDeleteBookingPaymentLoading } from '../../../../redux/selectors/bookingSelector';
import { useMediaQuery } from '@mantine/hooks';
import { ScrollArea } from '@mantine/core';

interface ManualBookingRequest extends Booking {
    generateNewPaymentLink?: boolean;
    sendEmailOnBooking?: boolean;
    showPaymentLinkInEmail?: boolean;
    forceUpdateShowCapacity?: boolean;
}

interface AddEditBookingProps {
    opened: boolean;
    onClose: () => void;
    activeBooking: Partial<ManualBookingRequest> | null;
    showDate?: Date | null;
}

function AddEditBooking({ opened, onClose, activeBooking, showDate }: AddEditBookingProps) {
    const dispatch = useDispatch();
    const user = useSelector(selectUserInfo);
    const userList: User[] = useSelector(selectUsersSelector);
    const settings = useSelector(selectSettingsSelector);
    const deleteBookingPaymentLoading = useSelector(selectDeleteBookingPaymentLoading);
    const isMobile = useMediaQuery('(max-width: 50em)');

    const { childTicketPrice, standardTicketPrice, vipTicketPrice, vipTicketChildPrice, additionalFeePercentage } = useMemo(() => {
        let childTicketPrice = 65;
        let standardTicketPrice = 80;
        let vipTicketPrice = 105;
        let vipTicketChildPrice = 90;
        let additionalFeePercentage = 2.24;

        settings.forEach((setting: Setting) => {
            if (setting.setting_key === SettingKey.CHILD_TICKET_PRICE) {
                childTicketPrice = setting.setting_value as number;
            }
            if (setting.setting_key === SettingKey.STANDARD_TICKET_PRICE) {
                standardTicketPrice = setting.setting_value as number;
            }
            if (setting.setting_key === SettingKey.VIP_TICKET_PRICE) {
                vipTicketPrice = setting.setting_value as number;
            }
            if (setting.setting_key === SettingKey.VIP_TICKET_CHILD_PRICE) {
                vipTicketChildPrice = setting.setting_value as number;
            }
            if (setting.setting_key === SettingKey.ADDITIONAL_FEE_PERCENTAGE) {
                additionalFeePercentage = setting.setting_value as number;
            }
        });

        return { childTicketPrice, standardTicketPrice, vipTicketPrice, vipTicketChildPrice, additionalFeePercentage }
    }, [settings]);

    const emptyBooking: Partial<ManualBookingRequest> = {
        show_id: '',
        booking_type: BookingType.STANDARD,
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        booking_date: null as any,
        created_source: BookingSource.BACKOFFICE,
        language_code: 'en',
        total_price: 0,
        payment_status: BookingPaymentStatus.PENDING,
        booked_adult_seats: 0,
        booked_children_seats: 0,
        booked_total_seats: 0,
        seat_ids: [],
        is_checked_in: false,
        created_by: user?._id || '',
        notes: '',
        sendEmailOnBooking: false,
        showPaymentLinkInEmail: false
    };

    const [booking, setBooking] = useState<Partial<ManualBookingRequest>>(emptyBooking);
    const [isAdditionalFeesApplied, setIsAdditionalFeesApplied] = useState(!activeBooking);

    const getPriceWithFees = (totalPrice: number) => {
        return totalPrice + ((totalPrice * additionalFeePercentage) / 100);
    }

    const isPaidOrConfirmedStandardTicket = useMemo(() => activeBooking?.booking_type?.toLowerCase() === BookingType.STANDARD.toLowerCase()
        && (activeBooking?.payment_status?.toLowerCase() === BookingPaymentStatus.PAID.toLowerCase()
            || activeBooking?.payment_status?.toLowerCase() === BookingPaymentStatus.CONFIRMED.toLowerCase()), [activeBooking?.payment_status, activeBooking?.booking_type]);

    const generatePaymentUrlLabel = useMemo(() => {
        if (isPaidOrConfirmedStandardTicket && booking?.booking_type?.toLowerCase() === BookingType.VIP.toLowerCase()) {
            const oldPrice = activeBooking?.total_price || 0;
            const newPrice = isAdditionalFeesApplied ? Number(getPriceWithFees(booking?.total_price || 0).toFixed(2)) : booking?.total_price || 0;
            const priceToPay = newPrice - oldPrice;
            return 'Generate new VIP Upgrade payment link of ' + priceToPay.toFixed(2) + '€';
        }
        return 'Generate new Payment Link';
    }, [isPaidOrConfirmedStandardTicket, booking?.booking_type, booking?.total_price, activeBooking?.total_price, additionalFeePercentage, isAdditionalFeesApplied])

    const handleSubmit = () => {
        if (activeBooking) {
            dispatch(updateBooking({
                id: activeBooking._id as string,
                data: {
                    ...booking,
                    total_price: isAdditionalFeesApplied ? Number(getPriceWithFees(booking.total_price || 0)?.toFixed(2) || 0) : booking.total_price
                }
            } as any));
        } else {
            const eventDate = getAthensDate(booking.booking_date, false) as DateTime;
            const showTime = getShowTimeForDate(eventDate.toJSDate());
            const [hours, minutes] = showTime.split(':').map(Number);
            const athensTime = eventDate.set({ hour: hours, minute: minutes });
            const bookingDate = athensTime.setZone('UTC').toJSDate();

            dispatch(createManualBooking({
                ...booking,
                booking_date: bookingDate,
                created_by: user?._id || '',
                total_price: isAdditionalFeesApplied ? Number(getPriceWithFees(booking.total_price || 0)?.toFixed(2) || 0) : booking.total_price
            } as any));
        }
        onClose();
        setBooking(emptyBooking);
    };

    const createdByUser = useMemo(() => userList.find(user => user._id === booking.created_by), [userList, booking.created_by]);

    const eventDateChanged = useMemo(() => {
        if (activeBooking) {
            return !areAthensDatesEqual(activeBooking?.booking_date as any, booking.booking_date as any);
        }
        return false;
    }, [activeBooking, booking.booking_date]);

    const seatsChanged = useMemo(() => {
        if (activeBooking) {
            return activeBooking.booked_total_seats !== booking.booked_total_seats || activeBooking.booked_adult_seats !== booking.booked_adult_seats || activeBooking.booked_children_seats !== booking.booked_children_seats;
        }
        return false;
    }, [activeBooking, booking.booked_total_seats, booking.booked_adult_seats, booking.booked_children_seats]);


    const updateShowCapacityWhenFromPendingToPaidConfirmed = useMemo(() => {
        return activeBooking?.payment_status === BookingPaymentStatus.PENDING
            && (booking?.payment_status === BookingPaymentStatus.PAID || booking?.payment_status === BookingPaymentStatus.CONFIRMED);
    }, [activeBooking, booking.payment_status]);

    const seatsChangedOrEventDateChangedOrForceUpdateShowCapacity = useMemo(() => {
        return seatsChanged || eventDateChanged || (updateShowCapacityWhenFromPendingToPaidConfirmed && booking.forceUpdateShowCapacity);
    }, [seatsChanged, eventDateChanged, updateShowCapacityWhenFromPendingToPaidConfirmed, booking.forceUpdateShowCapacity]);

    useEffect(() => {
        if (activeBooking) {
            setBooking({
                ...activeBooking,
                generateNewPaymentLink: false,
                sendEmailOnBooking: false,
                showPaymentLinkInEmail: false,
                forceUpdateShowCapacity: false
            });
        } else {
            setBooking({ ...emptyBooking, booking_date: showDate || null as any });
        }
        setIsAdditionalFeesApplied(!activeBooking);
    }, [activeBooking, showDate]);

    useEffect(() => {
        if (!userList.length) {
            dispatch(getAllUsers());
        }
    }, [])

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={activeBooking ? `Edit Booking ${activeBooking.short_id} - Created by ${createdByUser ? `${createdByUser?.first_name} ${createdByUser?.last_name}` : activeBooking.created_by || 'Unknown'}` : 'Add New Booking'}
            size="xl"
            radius="md"
            fullScreen={isMobile}
            transitionProps={{ transition: 'fade', duration: 200 }}
            scrollAreaComponent={ScrollArea.Autosize}
            removeScrollProps={{ allowPinchZoom: true }}
        >
            <Group mb="md">
                <Stack gap="md" flex={1}>
                    <Select
                        mt={-35}
                        label="Booking Type"
                        placeholder="Select booking type"
                        data={Object.values(BookingType).map(type => ({
                            value: type,
                            label: type.toUpperCase()
                        }))}
                        value={booking.booking_type}
                        onChange={(value) => {
                            const bookingType = value as BookingType;
                            const totalPrice = bookingType === BookingType.VIP
                                ? ((booking.booked_adult_seats || 0) * vipTicketPrice) + ((booking.booked_children_seats || 0) * vipTicketChildPrice)
                                : ((booking.booked_adult_seats || 0) * standardTicketPrice) + ((booking.booked_children_seats || 0) * childTicketPrice);
                            setBooking({
                                ...booking,
                                booking_type: bookingType,
                                total_price: totalPrice,
                            });
                        }}
                        required
                    />
                    <TextInput
                        label="Customer Name"
                        placeholder="Enter customer name"
                        value={booking.customer_name}
                        onChange={(e) => setBooking({ ...booking, customer_name: e.target.value })}
                        required
                    />
                    <TextInput
                        label="Customer Email"
                        placeholder="Enter customer email"
                        value={booking.customer_email}
                        onChange={(e) => setBooking({ ...booking, customer_email: e.target.value })}
                        required
                    />
                    <TextInput
                        label="Customer Phone"
                        placeholder="Enter customer phone"
                        value={booking.customer_phone}
                        onChange={(e) => setBooking({ ...booking, customer_phone: e.target.value })}
                        required
                    />
                    <DatePickerInput
                        label="Event Date"
                        placeholder="Select date"
                        value={booking.booking_date ? new Date(booking.booking_date) : null}
                        onChange={(value) => {
                            if (value) {
                                const eventDate = getAthensDate(value, false) as DateTime;
                                const showTime = getShowTimeForDate(eventDate.toJSDate());
                                const [hours, minutes] = showTime.split(':').map(Number);
                                const athensTime = eventDate.set({ hour: hours, minute: minutes });
                                const bookingDate = athensTime.setZone('UTC').toJSDate();
                                setBooking({ ...booking, booking_date: bookingDate });
                            } else {
                                setBooking({ ...booking, booking_date: null as any });
                            }
                        }}
                        required
                        disabled={!!showDate || seatsChanged || (updateShowCapacityWhenFromPendingToPaidConfirmed && booking.forceUpdateShowCapacity)}
                    />
                </Stack>
                <Stack gap="md" flex={1}>
                    <Select
                        label="Booking Source"
                        placeholder="Select booking source"
                        data={Object.values(BookingSource)
                            .filter(source => source !== BookingSource.WEBSITE)
                            .map(source => ({
                                value: source,
                                label: source.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
                            }))}
                        value={booking.created_source}
                        onChange={(value) => setBooking({ ...booking, created_source: value as BookingSource })}
                        required
                    />
                    <NumberInput
                        label="Adult Seats"
                        placeholder="Enter number of adult seats"
                        disabled={eventDateChanged || (updateShowCapacityWhenFromPendingToPaidConfirmed && booking.forceUpdateShowCapacity)}
                        value={booking.booked_adult_seats}
                        onChange={(value) => {
                            const newBookedSeats = Number(value);
                            if (booking.booking_type === BookingType.STANDARD) {
                                const totalPrice = (newBookedSeats * standardTicketPrice) + ((booking.booked_children_seats || 0) * childTicketPrice);
                                setBooking({
                                    ...booking,
                                    booked_adult_seats: newBookedSeats,
                                    booked_total_seats: newBookedSeats + (booking.booked_children_seats || 0),
                                    total_price: totalPrice
                                });
                            } else {
                                const totalPrice = (newBookedSeats * vipTicketPrice) + ((booking.booked_children_seats || 0) * vipTicketChildPrice);
                                setBooking({
                                    ...booking,
                                    booked_adult_seats: newBookedSeats,
                                    booked_total_seats: newBookedSeats + (booking.booked_children_seats || 0),
                                    total_price: totalPrice
                                });
                            }
                        }}
                        min={0}
                        required
                    />
                    <NumberInput
                        label="Children Seats"
                        placeholder="Enter number of children seats"
                        value={booking.booked_children_seats}
                        disabled={eventDateChanged || (updateShowCapacityWhenFromPendingToPaidConfirmed && booking.forceUpdateShowCapacity)}
                        onChange={(value) => {
                            const newChildrenSeats = Number(value);
                            if (booking.booking_type === BookingType.STANDARD) {
                                const totalPrice = (booking.booked_adult_seats || 0) * standardTicketPrice + (newChildrenSeats * childTicketPrice);
                                setBooking({
                                    ...booking,
                                    booked_children_seats: newChildrenSeats,
                                    booked_total_seats: (booking.booked_adult_seats || 0) + newChildrenSeats,
                                    total_price: totalPrice
                                });
                            } else {
                                const totalPrice = (booking.booked_adult_seats || 0) * vipTicketPrice + (newChildrenSeats * vipTicketChildPrice);
                                setBooking({
                                    ...booking,
                                    booked_children_seats: newChildrenSeats,
                                    booked_total_seats: (booking.booked_adult_seats || 0) + newChildrenSeats,
                                    total_price: totalPrice
                                });
                            }
                        }}
                        min={0}
                        max={5}
                        required
                    />
                    <Select
                        label="Payment Status"
                        placeholder="Select payment status"
                        data={Object.values(BookingPaymentStatus).map(status => ({
                            value: status,
                            label: status.charAt(0).toUpperCase() + status.slice(1)
                        }))}
                        value={booking.payment_status}
                        onChange={(value) => setBooking({ ...booking, payment_status: value as BookingPaymentStatus })}
                        required
                    />
                    <Stack>
                        <NumberInput
                            label="Total Price"
                            placeholder="Enter total price"
                            value={booking.total_price}
                            onChange={(value) => setBooking({ ...booking, total_price: Number(value) })}
                            min={0}
                            required
                        />
                        <Group justify="space-between">
                            <Text size="sm">Total Price: {isAdditionalFeesApplied ? getPriceWithFees(booking.total_price || 0)?.toFixed(2) : booking.total_price}</Text>
                            <Switch
                                size="sm"
                                label={`Enable fees ${additionalFeePercentage}%`}
                                checked={isAdditionalFeesApplied}
                                onChange={(e) => setIsAdditionalFeesApplied(e.currentTarget.checked)}
                            />
                        </Group>
                    </Stack>
                </Stack>
            </Group>
            <Stack gap="md">
                <Textarea
                    label="Notes"
                    placeholder="Enter notes"
                    value={booking.notes}
                    onChange={(e) => setBooking({ ...booking, notes: e.target.value })}
                />
                <Textarea
                    label="Payment url"
                    placeholder="No payment url generated"
                    value={booking.generated_payment_link}
                    onChange={(e) => setBooking({ ...booking, generated_payment_link: e.target.value })}
                    readOnly
                />
                <Group justify="space-between" align="center">
                    <Switch
                        size="md"
                        label={generatePaymentUrlLabel}
                        checked={booking.generateNewPaymentLink}
                        onChange={(e) => setBooking({ ...booking, generateNewPaymentLink: e.currentTarget.checked })}
                    />
                    <Group gap="md">
                        <CopyButton value={booking.generated_payment_link || ''} timeout={800}>
                            {({ copied, copy }) => (
                                <Button color={copied ? 'teal' : 'blue'} onClick={copy} disabled={!booking.generated_payment_link}>
                                    {copied ? 'Copied link' : 'Copy link'}
                                </Button>
                            )}
                        </CopyButton>
                        <Button
                            onClick={() => {
                                dispatch(deleteBookingPayment(activeBooking?._id as any));
                                onClose();
                            }}
                            disabled={!booking.generated_payment_link}
                            loading={deleteBookingPaymentLoading}
                            color="red"
                        >Delete Payment Link</Button>
                    </Group>
                </Group>

                <Switch
                    size="md"
                    label="Checked In"
                    checked={booking.is_checked_in}
                    onChange={(e) => setBooking({ ...booking, is_checked_in: e.currentTarget.checked })}
                />
                <Switch
                    disabled={!booking?.customer_email}
                    size="md"
                    label="Send Email on Booking"
                    checked={booking.sendEmailOnBooking}
                    onChange={(e) => setBooking({ ...booking, sendEmailOnBooking: e.currentTarget.checked })}
                />
                <Switch
                    disabled={!booking?.customer_email || !booking?.generateNewPaymentLink}
                    size="md"
                    label="Show Payment Link in Email"
                    checked={booking.showPaymentLinkInEmail}
                    onChange={(e) => setBooking({ ...booking, showPaymentLinkInEmail: e.currentTarget.checked })}
                />
                {updateShowCapacityWhenFromPendingToPaidConfirmed
                    && <Switch
                        size="md"
                        disabled={seatsChanged || eventDateChanged}
                        label="Force Update Show Capacity from PENDING to PAID/CONFIRMED"
                        description="If you want to update the show capacity for a PENDING booking that was created from the WEBSITE and is being set to PAID/CONFIRMED, you can check this option. This will update the show capacity for the booking. WARNING: use this option with caution, as it will update the show capacity for the booking."
                        checked={booking.forceUpdateShowCapacity}
                        onChange={(e) => setBooking({ ...booking, forceUpdateShowCapacity: e.currentTarget.checked })}
                    />}
                {isPaidOrConfirmedStandardTicket && booking?.booking_type?.toLowerCase() === BookingType.VIP.toLowerCase() && <Text c="red">Please avoid changing event date while upgrading to VIP, due to long operation time</Text>}

                {activeBooking && booking?.generateNewPaymentLink && <Text c="red">WARNING: Payment link is generated, in these cases: 1. Upgrading to VIP (STANDARD + PAID/CONFIRMED STATUS + GENERATE NEW PAYMENT LINK), 2. Not paid bookings(EXCLUDING PAID/CONFIRMED + GENERATE NEW PAYMENT LINK).</Text>}
                {seatsChangedOrEventDateChangedOrForceUpdateShowCapacity
                    && <Text c="red">WARNING: Seats changed OR event date changed OR force update show capacity from PENDING to PAID/CONFIRMED. ONE OPERATION AT A TIME ONLY.</Text>}
            </Stack>

            <Group justify="flex-end" mt="md">
                <Button variant="outline" onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit}>{activeBooking ? 'Update Booking' : 'Create Booking'}</Button>
            </Group>
        </Modal>
    );
}

export default AddEditBooking;